import {
  Anchor,
  Avatar,
  Badge,
  Box,
  Card,
  Divider,
  Flex,
  Group,
  Modal,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconCalendar,
  IconClock,
  IconLink,
  IconMapPin,
} from "@tabler/icons-react";
import speakerProfilePlaceholder from "../../assets/defaultProfile.png";
import sponsorProfilePlaceholder from "../../assets/defaultSponsorProfile.png";

import { getTranslatedDay, getTranslatedMonth } from "@congrs/utils";
import ExpandedRow from "../Sessions/ExpandedRow";
import { useTranslation } from "react-i18next";
import { getI18nText } from "../../utils/i18nUtils";

const SessionDetails = ({
  modalOpened,
  handleCloseModal,
  selectedSession = {},
}) => {
  const { t, i18n } = useTranslation();

  return (
    <Modal
      opened={modalOpened}
      onClose={handleCloseModal}
      size="lg"
      title={
        <Title order={2} style={{ color: "#667C99" }}>
          {t("session_details")}
        </Title>
      }
    >
      {selectedSession && (
        <Stack gap="lg">
          <Box>
            <Title order={3}>
              {getI18nText(
                selectedSession.titleI18n,
                selectedSession.title,
                i18n
              )}
            </Title>
            <Group gap="xl" mt="md">
              <Flex align="center" gap="xs">
                <IconCalendar size={18} color="#667C99" />
                <Text size="sm" fw={500}>
                  {`${getTranslatedDay(selectedSession.starting, t)}, ${getTranslatedMonth(selectedSession.starting, t)} ${String(new Date(selectedSession.starting).getDate()).padStart(2, "0")}, ${new Date(selectedSession.starting).getFullYear()}`}
                </Text>
              </Flex>
              <Flex align="center" gap="xs">
                <IconClock size={18} color="#667C99" />
                <Text size="sm" fw={500}>
                  {new Date(selectedSession.starting).toLocaleTimeString(
                    "en-US",
                    { hour: "2-digit", minute: "2-digit", hour12: false }
                  )}{" "}
                  -{" "}
                  {new Date(selectedSession.ending).toLocaleTimeString(
                    "en-US",
                    { hour: "2-digit", minute: "2-digit", hour12: false }
                  )}
                </Text>
              </Flex>
            </Group>
            <Flex align="center" gap="xs" mt="sm">
              <IconMapPin size={18} color="#667C99" />
              <Text size="sm" fw={500}>
                {getI18nText(
                  selectedSession.location.nameI18n,
                  selectedSession.location.name,
                  i18n
                )}
              </Text>
            </Flex>
            {selectedSession?.tags?.length > 0 && (
              <Group gap="xs" mt="md">
                {selectedSession.tags?.map((tag) => (
                  <Badge key={tag.id} variant="light" color="#667C99" size="lg">
                    {getI18nText(tag.titleI18n, tag.title, i18n)}
                  </Badge>
                ))}
              </Group>
            )}
          </Box>

          {selectedSession?.sessionLinks?.length > 0 && (
            <>
              <Divider />
              <Flex wrap={"wrap"} justify={"space-evenly"} gap={10}>
                {selectedSession.sessionLinks.map((link) => (
                  <Anchor
                    href={link.url || link.fileKey}
                    target="_blank"
                    c="dark"
                  >
                    <Group gap={3}>
                      <IconLink size={20} />
                      <Text size="md">{link.title}</Text>
                    </Group>
                  </Anchor>
                ))}
              </Flex>
            </>
          )}

          {selectedSession?.description && (
            <>
              <Divider />
              <ExpandedRow
                descTitleSize={"lg"}
                padding={"0"}
                description={getI18nText(
                  selectedSession.descriptionI18n,
                  selectedSession.description,
                  i18n
                )}
                descriptionRich={getI18nText(
                  selectedSession.descriptionRichI18n,
                  selectedSession.descriptionRich,
                  i18n
                )}
                descriptionText={t("description")}
                noDescriptionText={t("no_description_provided")}
              />
            </>
          )}

          {selectedSession?.speakers?.length > 0 && (
            <>
              <Divider />
              <Box>
                <Title size="lg" mb="md">
                  {selectedSession.speakers.length === 1
                    ? t("speaker")
                    : t("speakers")}
                </Title>
                <Stack gap="md">
                  {selectedSession.speakers.map((sessionSpeaker) => {
                    return (
                      <Card
                        key={sessionSpeaker.id}
                        padding="md"
                        radius="md"
                        withBorder
                      >
                        <Group gap="md">
                          <Avatar
                            src={
                              sessionSpeaker?.picture ||
                              speakerProfilePlaceholder
                            }
                            size={80}
                          />
                          <Box style={{ flex: 1 }}>
                            <Text fw={600} size="lg">
                              {sessionSpeaker.firstName}{" "}
                              {sessionSpeaker.lastName}
                            </Text>
                            {sessionSpeaker.occupation && (
                              <Text size="sm" c="dimmed">
                                {sessionSpeaker.occupation}
                              </Text>
                            )}
                          </Box>
                        </Group>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            </>
          )}
          {selectedSession?.sponsor?.id !== null && (
            <>
              <Divider />
              <Box>
                <Title order={4} mb="md">
                  {t("sponsor")}
                </Title>
                <Stack gap="md">
                  <Card
                    key={selectedSession.sponsorId}
                    padding="md"
                    radius="md"
                    withBorder
                  >
                    <Group gap="md">
                      <Avatar
                        src={
                          selectedSession?.sponsor.logo ||
                          sponsorProfilePlaceholder
                        }
                        size={80}
                      />
                      <Box style={{ flex: 1 }}>
                        <Text fw={600} size="lg" lineClamp={3}>
                          {selectedSession.sponsor.name}
                        </Text>
                      </Box>
                    </Group>
                  </Card>
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      )}
    </Modal>
  );
};

export default SessionDetails;
